import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack } from '@mui/material';

export interface SaleFormValues {
  date: string;
  quantity: number | '';
  pricePerShare: number | '';
}

interface SaleFormProps {
  open: boolean;
  initialValues?: SaleFormValues;
  onClose: () => void;
  onSubmit: (values: SaleFormValues) => void;
  availableShares?: number;
  isCrypto?: boolean;
}

export default function SaleForm({ open, initialValues, onClose, onSubmit, availableShares, isCrypto }: SaleFormProps) {
  const [values, setValues] = useState<SaleFormValues>(
    initialValues || { date: '', quantity: '', pricePerShare: '' }
  );

  // Update form values when initialValues changes
  useEffect(() => {
    if (initialValues) {
      setValues(initialValues);
    } else {
      setValues({ date: '', quantity: '', pricePerShare: '' });
    }
  }, [initialValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: name === 'quantity' || name === 'pricePerShare' ? (value === '' ? '' : Number(value)) : value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate quantity doesn't exceed available shares
    if (availableShares !== undefined && typeof values.quantity === 'number' && values.quantity > availableShares) {
      alert(`Não é possível vender ${values.quantity} unidades. Só estão disponíveis ${availableShares}.`);
      return;
    }
    
    onSubmit({ ...values });
  };

  const isQuantityError = availableShares !== undefined && typeof values.quantity === 'number' && values.quantity > availableShares;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{initialValues ? 'Editar venda' : 'Vender'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            {availableShares !== undefined && (
              <TextField
                label="Unidades disponíveis"
                value={availableShares}
                fullWidth
                disabled
                helperText="Máximo de unidades que pode vender"
              />
            )}
            <TextField
              label="Data"
              name="date"
              type="date"
              value={values.date}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              label="Quantidade"
              name="quantity"
              type="number"
              value={values.quantity}
              onChange={handleChange}
              fullWidth
              required
              error={isQuantityError}
              helperText={isQuantityError ? `Não pode exceder as ${availableShares} unidades disponíveis` : ''}
              inputProps={{ max: availableShares, step: isCrypto ? '0.000000001' : undefined, min: 0 }}
            />
            <TextField
              label="Preço por unidade"
              name="pricePerShare"
              type="number"
              value={values.pricePerShare}
              onChange={handleChange}
              fullWidth
              required
              inputProps={{ step: isCrypto ? '0.000000001' : 0.01, min: 0 }}
            />
            
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="error"
            disabled={isQuantityError}
          >
            {initialValues ? 'Atualizar venda' : 'Vender'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
